import type { ReactNode } from 'react';
import { VIEW_LABEL, type ViewKey } from '@/domain';
import { NAV_LABEL_OVERRIDE, TAB_LABEL_OVERRIDE } from '@/shared/strings';
import {
  ActivityIcon,
  ArchiveIcon,
  DashboardIcon,
  DoctorIcon,
  FinalCheckIcon,
  LineIcon,
  OrdersIcon,
  OverviewIcon,
  PatientIcon,
  PayIcon,
  PerfIcon,
  PlusIcon,
  ScanIcon,
  SettingsIcon,
  ShipIcon,
  StaffIcon,
  TagIcon,
  TrayIcon,
} from '../icons';

/**
 * The route each view opens. Section 5.
 *
 * Navigation is derived, never hard coded: the menu, the mobile tabs and the
 * route guard all read from `session.views`, and this map only says where a
 * granted view lives. Granting a screen to a role in the admin screen puts it
 * in that role's menu on the next render with no code change here.
 */
export const VIEW_PATH: Record<ViewKey, string> = {
  overview: '/',
  dashboard: '/dashboard',
  line: '/line',
  orders: '/orders',
  finalcheck: '/qc',
  reports: '/reports',
  archive: '/archive',
  activity: '/activity',
  staff: '/staff',
  users: '/users',
  settings: '/settings',
  items: '/items',
  intake: '/intake',
  shipping: '/shipping',
  payments: '/payments',
  onhold: '/on-hold',
  // Each list, with a record's profile under it: /doctors/:id and
  // /patients/:id. The request of 6 October 2026 (second), item 4.
  doctors: '/doctors',
  patients: '/patients',
  queue: '/queue',
  perf: '/perf',
  scan: '/scan',
  stage: '/line',
  job: '/queue',
  record: '/archive',
};

const ICONS: Partial<Record<ViewKey, ReactNode>> = {
  overview: <OverviewIcon />,
  dashboard: <DashboardIcon />,
  line: <LineIcon />,
  orders: <OrdersIcon />,
  finalcheck: <FinalCheckIcon />,
  reports: <PerfIcon />,
  archive: <ArchiveIcon />,
  activity: <ActivityIcon />,
  staff: <StaffIcon />,
  users: <StaffIcon />,
  settings: <SettingsIcon />,
  items: <TagIcon />,
  intake: <PlusIcon />,
  shipping: <ShipIcon />,
  payments: <PayIcon />,
  doctors: <DoctorIcon />,
  patients: <PatientIcon />,
  queue: <TrayIcon />,
  perf: <PerfIcon />,
  scan: <ScanIcon />,
};

export interface NavItem {
  view: ViewKey;
  path: string;
  label: string;
  icon: ReactNode;
}

export function navItemFor(view: ViewKey, short = false): NavItem {
  const override = short ? TAB_LABEL_OVERRIDE[view] : NAV_LABEL_OVERRIDE[view];
  return {
    view,
    path: VIEW_PATH[view],
    label: override ?? VIEW_LABEL[view],
    icon: ICONS[view] ?? <OrdersIcon />,
  };
}

export function navItemsFor(views: readonly ViewKey[], short = false): NavItem[] {
  return views.map((view) => navItemFor(view, short));
}
